import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FieldPath, FieldValues, useForm, UseFormReturn, useWatch } from 'react-hook-form';
import { useAuth } from '../../../context/Auth';
import { useQuery } from '@tanstack/react-query';
import { appAgentService } from '../../../services/central-api/app-agent';
import { ActivityIndicator, View } from 'react-native';
import { createStyleSheet, UnistylesRuntime, useStyles } from 'react-native-unistyles';
import PatrolIndicatorDropdown from '../../../components/app-agent/PatrolIndicatorDropdown';
import FormInput from '../../../components/FormInput';
import { translate } from '../../../services/translate';
import TimePicker from 'react-time-picker';
import MyAppText from '../../../components/MyAppText';
import { ScrollView } from 'react-native-web-hover';
import Toast from 'react-native-toast-message';
import { UserNotInUnit } from '../../../services/central-api/base-service';
import moment from 'moment';
import MyDatePicker from '../../../components/MyDatePicker';
import Divider from '../../../components/Divider';
import ActivitiesAndServicesLocationSelector from './ActivitiesAndServicesLocationSelector';
import FloatingButton from '../../../components/FloatingButton';
import Header from '../../../components/Header';
import CustomButton from '../../../components/CustomButton';
import DeleteActivitiesAndServicesModal from './DeleteActivitiesAndServicesModal';
import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { RootNavigation } from '../../../Routes';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { AppAgentParamList } from '../../../typings/Params';
import Container from '../../../components/Container';

interface ActivityAndServiceForm {
    cep: string;
    address: string;
    number: number | undefined;
    date?: Date;
    startTime: string;
    endTime: string;
    actionsQty: number | undefined;
    publicQty: number | undefined;
    documentNumber: number | undefined;
    indicatorId: number;
    latitude: string;
    longitude: string;
    cityPropertyId: number;
}

interface SectionHeaderParams {
    title: string;
    children: ReactNode;
    viewZIndex?: number;
    childrenZIndex?: number;
    required?: boolean;
}

function SectionHeader({ children, title, viewZIndex, childrenZIndex, required }: SectionHeaderParams) {
    const { styles, theme } = useStyles(styleSheet);

    return <View style={{ flexDirection: 'column', rowGap: 16, zIndex: viewZIndex }}>
        <View style={{ flexDirection: 'column', rowGap: 8, zIndex: viewZIndex ? viewZIndex : 2 }}>
            <View style={{ flexDirection: 'row', columnGap: 8, alignItems: 'center' }}>
                <View style={{ flexDirection: 'row', gap: 3, alignContent: 'center', alignItems: 'baseline' }}>
                    <MyAppText style={styles.sectionHeaderTitle}>{translate(title)}</MyAppText>
                    {required ?
                        <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                        : null}
                </View>
            </View>
            <Divider style={{ zIndex: -1 }} color={theme.colors.primary['200']} />
        </View>
        <View style={{ zIndex: childrenZIndex ?? 1, gap: 8 }}>
            {children}
        </View>
    </View>;
}

export default function ActivitiesAndServicesDetails({ navigation }: { navigation: RootNavigation; }) {
    const route = useRoute<RouteProp<AppAgentParamList, 'ActivitiesAndServicesDetails'>>();
    const activityId = route.params?.activityId;

    const { styles, theme } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();

    const isFocused = useIsFocused();
    const activityQuery = useQuery({
        queryKey: ['getActivityAndServiceById', activityId],
        queryFn: () => appAgentService.getActivityAndServiceById(activityId!),
        enabled: !!authenticatedUser && isFocused && !!activityId,
    });
    const { data } = activityQuery;

    const activeUnitQuery = useQuery({
        queryKey: ['getUserActiveUnit'],
        queryFn: () => appAgentService.getAgentActiveUnit(),
        enabled: !!authenticatedUser && isFocused,
    });
    const { data: activeUnit } = activeUnitQuery;

    const activityReady = !activityId || activityQuery.isFetched;
    const activeUnitReady = activeUnitQuery.isFetched;
    const queriesReady = !!authenticatedUser && activityReady && activeUnitReady;

    const isLoading = !!authenticatedUser && (activityQuery.isFetching || activeUnitQuery.isFetching);

    const dateIsNeeded = useMemo(() => {
        if (!activeUnit?.definitiveStart) {
            return false;
        }
        return moment().diff(moment(activeUnit?.definitiveStart), 'day') >= 1;
    }, [activeUnit?.definitiveStart]);

    const hasInitializedRef = useRef(false);
    useEffect(() => {
        hasInitializedRef.current = false;
    }, [activityId, authenticatedUser?.id]);

    useEffect(() => {
        if (!queriesReady || hasInitializedRef.current) {
            return;
        }
        hasInitializedRef.current = true;

        if (!data) {
            form.reset({
                ...defaultEmptyValues,
                date: dateIsNeeded ? moment().toDate() : undefined,
            });
            setPin(undefined);
            return;
        }

        settingCepInternallyRef.current = data.cep;
        form.reset({
            cep: data.cep,
            address: data.address,
            number: data.number,
            date: dateIsNeeded
                ? (data.startDate ? moment(data.startDate).toDate() : moment().toDate())
                : undefined,
            startTime: moment(data.startDate).format('HH:mm'),
            endTime: moment(data.endDate).format('HH:mm'),
            actionsQty: data.actionsQty,
            publicQty: data.publicQty,
            documentNumber: data.documentNumber,
            indicatorId: data.indicatorId,
            latitude: data.latitude,
            longitude: data.longitude,
        });
        if (data.latitude && data.longitude) {
            setPin({ lat: Number(data.latitude), lng: Number(data.longitude) });
        }
    }, [queriesReady, data, dateIsNeeded]);

    const [pin, setPin] = useState<LatLng | undefined>(undefined);
    const [sendingData, setSendingData] = useState<boolean>(false);
    const [datePickerZIndex, setDatePickerZIndex] = useState<number>(3);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const settingCepInternallyRef = useRef('');

    const defaultEmptyValues: ActivityAndServiceForm = {
        cep: '',
        address: '',
        number: undefined,
        date: undefined,
        startTime: '',
        endTime: '',
        actionsQty: undefined,
        publicQty: undefined,
        documentNumber: undefined,
        indicatorId: 0,
        latitude: '',
        longitude: '',
        cityPropertyId: 0
    };

    const form = useForm<ActivityAndServiceForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    useEffect(() => {
        form.register('address', { required: true });
        form.register('latitude', { required: true });
        form.register('longitude', { required: true });
    }, [form]);

    function clearValues() {
        form.reset(defaultEmptyValues);
        setPin(undefined);
    }

    function formatDuration(totalMinutes: number): string {
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        if (hours === 0) {
            return `${minutes}min`;
        }
        if (minutes === 0) {
            return `${hours}h`;
        }
        return `${hours}h${minutes}min`;
    }

    function anchorEndTime(endTime: string, start: moment.Moment): moment.Moment {
        const [hours, minutes] = endTime.split(':').map(Number);
        let end = start.clone().set({ hour: hours, minute: minutes, second: 0, millisecond: 0 });

        if (end.isBefore(start)) {
            end = end.add(1, 'day');
        }

        return end;
    }

    function computeEffectiveRange(startTime?: string, endTime?: string, date?: Date) {
        if (!startTime || !endTime) {
            return null;
        }

        let start: moment.Moment | null = null;

        if (dateIsNeeded) {
            if (!date) {
                return null;
            }
            const [hours, minutes] = startTime.split(':').map(Number);
            start = moment(date).set({ hour: hours, minute: minutes, second: 0, millisecond: 0 });
        } else if (activeUnit?.definitiveStart) {
            const shiftStart = moment(activeUnit.definitiveStart);
            const [hours, minutes] = startTime.split(':').map(Number);

            start = shiftStart.clone().set({ hour: hours, minute: minutes, second: 0, millisecond: 0 });

            if (start.isBefore(shiftStart)) {
                start = start.add(1, 'day');
            }
        }

        if (!start) {
            return null;
        }

        const end = anchorEndTime(endTime, start);

        return { start, end };
    }

    async function formSubmit() {
        setSendingData(true);
        try {
            const values = form.getValues();
            const dates = computeEffectiveRange(values.startTime, values.endTime, values.date);
            const resolvedDate = dates?.start
                ? moment(dates.start).format('YYYY-MM-DD')
                : undefined;

            if (activityId) {
                await appAgentService.updateActivityAndService({
                    id: activityId,
                    cep: values.cep ? values.cep : undefined,
                    address: values.address ? values.address : undefined,
                    number: values.number ? Number(values.number!) : undefined,
                    date: resolvedDate,
                    startTime: values.startTime ? values.startTime : undefined,
                    endTime: values.endTime ? values.endTime : undefined,
                    actionsQty: values.actionsQty ? Number(values.actionsQty!) : undefined,
                    publicQty: values.publicQty ? Number(values.publicQty!) : undefined,
                    documentNumber: values.documentNumber ? Number(values.documentNumber) : undefined,
                    indicatorId: values.indicatorId ? values.indicatorId : undefined,
                    latitude: values.latitude ? Number(values.latitude) : undefined,
                    longitude: values.longitude ? Number(values.longitude) : undefined,
                });
            } else {
                await appAgentService.createActivityAndService({
                    cep: values.cep,
                    address: values.address,
                    number: values.number ? Number(values.number!) : undefined,
                    date: resolvedDate,
                    startTime: values.startTime,
                    endTime: values.endTime,
                    actionsQty: Number(values.actionsQty!),
                    publicQty: Number(values.publicQty!),
                    documentNumber: values.documentNumber ? Number(values.documentNumber) : undefined,
                    indicatorId: values.indicatorId,
                    latitude: Number(values.latitude),
                    longitude: Number(values.longitude),
                });
            }

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            clearValues();
            navigation.navigate('AppAgent', {
                screen: 'ActivitiesAndServices'
            });
        } catch (err) {
            if (err instanceof UserNotInUnit) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate('notActiveInUnit'),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setSendingData(false);
        }
    }

    const values = useWatch({ control: form.control });
    function makeSetStateHandler<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>(
        form: UseFormReturn<TFieldValues>,
        fieldName: TName
    ) {
        return (value: React.SetStateAction<TFieldValues[TName]>) => {
            const resolved = typeof value === 'function'
                ? (value as (prev: TFieldValues[TName]) => TFieldValues[TName])(form.getValues(fieldName))
                : value;

            form.setValue(fieldName, resolved, {
                shouldValidate: true,
                shouldDirty: true,
            });
        };
    }

    const activityRange = useMemo(
        () => computeEffectiveRange(values.startTime, values.endTime, values.date),
        [values.startTime, values.endTime, values.date, dateIsNeeded, activeUnit?.definitiveStart, data?.startDate]
    );

    const activityRangeLabel = useMemo(() => {
        if (!activityRange) {
            return null;
        }

        const { start, end } = activityRange;
        const crossesMidnight = !start.isSame(end, 'day');
        const isSameValue = start.isSame(end);
        const durationMinutes = end.diff(start, 'minutes');

        return {
            rangeText: crossesMidnight
                ? `${start.format('DD/MM HH:mm')} → ${end.format('DD/MM HH:mm')}`
                : `${start.format('DD/MM')} · ${start.format('HH:mm')} → ${end.format('HH:mm')}`,
            durationText: formatDuration(durationMinutes),
            crossesMidnight,
            isSameValue
        };
    }, [activityRange]);

    return (
        <Container>
            <View style={[styles.formBody, isLoading && { height: '100%' }]}>
                <Header
                    title={translate('activityAndService')}
                    goBackAction={() => {
                        clearValues();
                        navigation.navigate('AppAgent', {
                            screen: 'ActivitiesAndServices'
                        });
                    }}
                />

                <ScrollView
                    showsVerticalScrollIndicator={false}
                    contentContainerStyle={[{ flexDirection: 'column', gap: 24, marginBottom: 104 }, isLoading && { flex: 1 }]}
                >
                    {isLoading
                        ? <View style={styles.loadingContainer}>
                            <ActivityIndicator size='large' color={theme.colors.iconColor} />
                        </View>
                        : <>
                            <Controller
                                control={form.control}
                                name="indicatorId"
                                rules={{
                                    validate: (value) => value !== 0 || translate('required_field')
                                }}
                                render={({ field }) => (
                                    <PatrolIndicatorDropdown
                                        value={field.value}
                                        setValue={makeSetStateHandler(form, 'indicatorId')}
                                        zIndex={4}
                                        required
                                        labelStyle={styles.fieldsLabelStyle}
                                        viewStyle={styles.itemView}
                                    />
                                )}
                            />
                            <SectionHeader
                                title='localization'
                                viewZIndex={3}
                                childrenZIndex={3}
                            >
                                <ActivitiesAndServicesLocationSelector
                                    form={form}
                                    lastCep={settingCepInternallyRef.current}
                                    setCep={(val) => settingCepInternallyRef.current = val}
                                    pin={pin}
                                    setPin={setPin}
                                    isEditing={!!activityId}
                                />
                            </SectionHeader>
                            <SectionHeader
                                title='activityData'
                                viewZIndex={datePickerZIndex - 1}
                                childrenZIndex={datePickerZIndex}

                            >
                                {dateIsNeeded && <MyDatePicker
                                    form={form}
                                    dateFieldName='date'
                                    required
                                    label={translate('activityDate')}
                                    hintText={translate('activityDateHint')}
                                    labelStyle={styles.fieldsLabelStyle}
                                    viewStyle={styles.itemView}
                                    minDate={activeUnit?.definitiveStart ? moment(activeUnit?.definitiveStart).toDate() : undefined}
                                    maxDate={moment().toDate()}
                                    zIndex={2}
                                    onCalendarOpen={() => setDatePickerZIndex(99999)}
                                    onCalendarClose={() => setDatePickerZIndex(2)}
                                />}
                                <View style={{ flexDirection: 'row', gap: 12 }}>
                                    <Controller
                                        control={form.control}
                                        name="startTime"
                                        rules={{ required: translate('required_field') }}
                                        render={({ field, fieldState }) => (
                                            <View style={{ flexDirection: 'column', gap: 8, flex: 1 }}>
                                                <View style={[{ flexDirection: 'row', columnGap: 5 }]}>
                                                    <MyAppText style={styles.fieldsLabelStyle}>{translate('startTime')}</MyAppText>
                                                    <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                                                </View>
                                                <TimePicker
                                                    disableClock={true}
                                                    format='HH:mm'
                                                    locale='pt-BR'
                                                    maxDetail='minute'
                                                    className={['light', 'lightAlt'].includes(UnistylesRuntime.themeName) ? ['inputColor', `inputColorLight`] : ['inputColor', `inputColorDark`]}
                                                    value={field.value}
                                                    clearIcon={null}
                                                    onChange={(value) => {
                                                        if (value) {
                                                            field.onChange(value);
                                                            form.trigger('endTime');
                                                        }
                                                    }}
                                                />
                                                {fieldState.error && (
                                                    <MyAppText style={styles.error}>{fieldState.error.message}</MyAppText>
                                                )}
                                            </View>
                                        )}
                                    />
                                    <Controller
                                        control={form.control}
                                        name="endTime"
                                        rules={{
                                            required: translate('required_field'),
                                            validate: (value) => {
                                                const startTime = form.getValues('startTime');
                                                if (startTime && value && startTime === value) {
                                                    return false;
                                                }
                                                return true;
                                            },
                                        }}
                                        render={({ field }) => (
                                            <View style={{ flexDirection: 'column', gap: 8, flex: 1 }}>
                                                <View style={[{ flexDirection: 'row', columnGap: 5 }]}>
                                                    <MyAppText style={styles.fieldsLabelStyle}>{translate('endTime')}</MyAppText>
                                                    <MyAppText style={{ color: theme.colors.errors['300'] }}>(*)</MyAppText>
                                                </View>
                                                <TimePicker
                                                    disableClock={true}
                                                    format='HH:mm'
                                                    locale='pt-BR'
                                                    maxDetail='minute'
                                                    className={['light', 'lightAlt'].includes(UnistylesRuntime.themeName) ? ['inputColor', `inputColorLight`] : ['inputColor', `inputColorDark`]}
                                                    value={field.value}
                                                    clearIcon={null}
                                                    onChange={(value) => {
                                                        if (value) {
                                                            field.onChange(value);
                                                            form.trigger('startTime');
                                                        }
                                                    }}
                                                />
                                            </View>
                                        )}
                                    />
                                </View>
                                {activityRangeLabel && (
                                    <View style={styles.rangePreview}>
                                        <MyAppText style={styles.rangePreviewText}>{activityRangeLabel.rangeText}</MyAppText>
                                        <MyAppText style={styles.rangePreviewDuration}>
                                            {translate('duration')}: {activityRangeLabel.durationText}
                                        </MyAppText>
                                        {activityRangeLabel.isSameValue
                                            ? <MyAppText style={styles.error}>
                                                {translate('startEndTimeCannotBeEqual')}
                                            </MyAppText>
                                            : activityRangeLabel.crossesMidnight && (
                                                <MyAppText style={styles.rangePreviewWarning}>
                                                    {translate('activityCrossesMidnightWarning')}
                                                </MyAppText>
                                            )}
                                    </View>
                                )}
                                <View style={{ flexDirection: 'row', gap: 12, alignItems: 'center' }}>
                                    <FormInput
                                        form={form}
                                        fieldName='actionsQty'
                                        label={translate('actionsQuantity')}
                                        mode='NUMBER'
                                        labelStyle={styles.fieldsLabelStyle}
                                        viewStyle={styles.itemView}
                                        required
                                    />
                                    <FormInput
                                        form={form}
                                        fieldName='publicQty'
                                        label={translate('publicQuantity')}
                                        mode='NUMBER'
                                        labelStyle={[styles.fieldsLabelStyle, { fontSize: 13 }]}
                                        viewStyle={styles.itemView}
                                        required
                                    />
                                </View>
                            </SectionHeader>
                            <SectionHeader
                                title='document'
                            >
                                <FormInput
                                    form={form}
                                    fieldName='documentNumber'
                                    label={translate('document')}
                                    mode='NUMBER'
                                    labelStyle={[styles.fieldsLabelStyle, { fontSize: 13 }]}
                                    viewStyle={styles.itemView}
                                />
                            </SectionHeader>
                        </>}
                </ScrollView>
            </View>
            <View style={styles.footer}>
                <View style={styles.footerView}>
                    <CustomButton
                        contentContainerStyle={{ width: '100%', height: '100%', minHeight: 56 }}
                        style={{ minHeight: 56 }}
                        text={translate(!activityId ? 'save' : 'saveChanges')}
                        type='primary'
                        onPress={formSubmit}
                        disabled={isLoading || !form.formState.isValid}
                        isLoading={isLoading || sendingData}
                    />
                </View>
            </View>
            <FloatingButton
                visible={!!activityId && !isLoading}
                actions={[
                    {
                        name: 'test_delete',
                        label: translate('deleteService'),
                        color: '#A14C4C',
                        icon: faTrash,
                        onPress: () => {
                            setIsDeleteModalVisible(true);
                        }
                    }
                ]}
                offset={{ vertical: 120 }}
            />
            {data && <DeleteActivitiesAndServicesModal
                isModalVisible={isDeleteModalVisible}
                setModalVisible={setIsDeleteModalVisible}
                onConfirm={() => {
                    setIsDeleteModalVisible(false);
                    clearValues();
                    navigation.navigate('AppAgent', {
                        screen: 'ActivitiesAndServices'
                    });
                }}
                selectedActivity={data}
            />}
        </Container>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    formBody: {
        backgroundColor: theme.colors.card.background,
        padding: 16,
        flex: 1,
        gap: 16
    },
    header: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    subheader: {
        fontSize: 16,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
    },
    fieldsLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    },
    itemView: {
        gap: 8,
        flex: 1
    },
    loadingContainer: {
        flexDirection: 'column',
        flex: 1,
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center'
    },
    error: {
        color: theme.colors.errors['300'],
        fontSize: 10,
    },
    rangePreview: {
        borderWidth: 1,
        borderColor: theme.colors.card.border,
        borderRadius: 8,
        paddingHorizontal: 12,
        paddingVertical: 10,
        gap: 4,
    },
    rangePreviewText: {
        color: theme.colors.neutral['500'],
        fontSize: 14,
        fontWeight: '600',
    },
    rangePreviewDuration: {
        color: theme.colors.neutral['400'],
        fontSize: 12,
    },
    rangePreviewWarning: {
        color: theme.colors.warnings['300'],
        fontSize: 11,
        fontWeight: '600',
    },
    sectionHeaderTitle: {
        color: theme.colors.primary['500'],
        fontWeight: '600',
        fontSize: 16,
    },
    footer: {
        position: 'absolute',
        bottom: 0,
        height: 104,
        width: '100%',
        backgroundColor: theme.colors.white['500']
    },
    footerView: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingVertical: 24,
        paddingHorizontal: 16
    }
}));
